import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useToast } from './ui/use-toast'

interface Company {
  id: string
  company_name: string
  logo_url: string | null
  remote_locations: string[] | null
  remote_locations_updated_at: string | null
}

function RemoteCompanies() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [uniqueLocationsCount, setUniqueLocationsCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  useEffect(() => {
    fetchCompanies()
  }, [])

  async function fetchCompanies() {
    try {
      setLoading(true)

      // Get total count of remote-first companies
      const { count, error: countError } = await supabase
        .from('companies')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true)
        .eq('hires_remote', true)

      if (countError) {
        console.error('Error fetching company count:', countError)
      } else {
        setTotalCount(count || 0)
      }

      // Fetch from companies table with specific columns, filtered by hires_remote
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('slug, name, logo_url, remote_locations, remote_locations_updated_at')
        .eq('is_active', true)
        .eq('hires_remote', true)

      if (companiesError) {
        console.error('Error fetching companies:', companiesError)
        throw companiesError
      }

      // Map to the Company interface
      const mappedCompanies = companiesData?.map(company => ({
        id: company.slug,
        company_name: company.name,
        logo_url: company.logo_url,
        remote_locations: company.remote_locations,
        remote_locations_updated_at: company.remote_locations_updated_at
      })) || []

      // Sort by number of remote locations (descending - most locations first)
      mappedCompanies.sort((a, b) => {
        const aCount = a.remote_locations?.length || 0
        const bCount = b.remote_locations?.length || 0
        return bCount - aCount
      })

      // Calculate unique locations across all companies
      const allLocations = new Set<string>()
      mappedCompanies.forEach(company => {
        company.remote_locations?.forEach((location: string) => {
          allLocations.add(location)
        })
      })
      setUniqueLocationsCount(allLocations.size)

      setCompanies(mappedCompanies)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch companies')
      console.error('Error fetching companies:', err)
    } finally {
      setLoading(false)
    }
  }

  function getCompanyInitials(companyName: string): string {
    const words = companyName.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  function formatRemoteLocations(locations: string[] | null): string {
    if (!locations || locations.length === 0) return 'N/A'
    // Show all locations, comma-separated
    return locations.join(', ')
  }

  function handleCompanyClick(company: Company) {
    // Open company page in new tab
    window.open(`/company/${encodeURIComponent(company.id)}`, '_blank')
  }

  function handleImageError(companyId: string) {
    setFailedImages(prev => new Set(prev).add(companyId))
  }

  if (loading) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="mb-8">
            <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-6 w-96 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="h-96 bg-gray-100 animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            Error: {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white pb-8">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Remote Opportunities
          </h1>
          <p className="text-lg text-gray-600 flex items-center gap-2">
            <span>{totalCount.toLocaleString()} companies hiring remotely in {uniqueLocationsCount} {uniqueLocationsCount === 1 ? 'location' : 'locations'} 🌎</span>
          </p>
        </div>

        {/* Companies Table */}
        {companies.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
            No remote companies available yet. Check back soon!
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hiring Remotely In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Last Updated
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    onClick={() => handleCompanyClick(company)}
                    className="cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    {/* Company Name with Logo */}
                    <td className="px-6 py-2 align-middle">
                      <div className="flex items-center gap-3">
                        {company.logo_url && !failedImages.has(company.id) ? (
                          <img
                            src={company.logo_url}
                            alt={company.company_name}
                            className="w-8 h-8 rounded-lg object-contain bg-white shrink-0"
                            onError={() => handleImageError(company.id)}
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white text-sm font-semibold shrink-0">
                            {getCompanyInitials(company.company_name)}
                          </div>
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {company.company_name}
                        </span>
                      </div>
                    </td>

                    {/* Remote Locations */}
                    <td className="px-6 py-2 text-sm text-gray-600 align-middle">
                      <div className="whitespace-normal leading-relaxed">
                        {formatRemoteLocations(company.remote_locations)}
                      </div>
                    </td>

                    {/* Last Updated */}
                    <td className="px-6 py-2 text-sm text-gray-500 align-middle whitespace-nowrap">
                      {formatDate(company.remote_locations_updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default RemoteCompanies
